import { useMutation, useQuery } from "@tanstack/react-query";
import { returnsAccountabilityService } from "modules/Pharmacy/ReturnsLog/api/returnsAccountibility.api";
import { GetUserData } from "common/service/CommonService";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_MODULES, PERMISSION_ACTIONS } from "common/constants/PermissionConstants";

interface ApiResponse<T = any> {
  data: T;
  message: string;
  statuscode: number;
}

export const useReturnsAccountability = ({
  searchText,
  pageNumber = 1,
  pageSize = 10,
  studyid,
  subjectno,
  returnid,
  getStudyDropDown,
  ipcategory,
  returnSelectAll = false,
}: {
  searchText?: string | null;
  pageNumber?: number;
  pageSize?: number;
  studyid?: number | string | null;
  subjectno?: string | null;
  returnid?: number | null;
  getStudyDropDown?: boolean | null;
  ipcategory?: string | null;
  returnSelectAll?: boolean | null;
} = {}) => {
  const { hasPermission } = usePermission();
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);

  /* =========================
     INSERT
  ========================= */
  const InsertMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: returnsAccountabilityService.insert,
  });

  /* =========================
     UPDATE
  ========================= */
  const UpdateMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: returnsAccountabilityService.update,
  });

  /* =========================
     DELETE
  ========================= */
  const DeleteMutation = useMutation<ApiResponse, Error, any>({
    mutationFn: returnsAccountabilityService.delete,
  });

  /* =========================
     SELECT ONE (useQuery)
  ========================= */
  const SelectQuery = useQuery<ApiResponse>({
    queryKey: ["returns-accountability-select", returnid],
    queryFn: () =>
      returnsAccountabilityService.select({
        returnid,
      }),
    enabled: !!returnid,
  });

  /* =========================
     SELECT ALL
  ========================= */
  const SelectAllQuery = useQuery<ApiResponse>({
    queryKey: [
      "returns-accountability-select-all",
      { searchText, pageNumber, pageSize, studyid, subjectno },
    ],
    queryFn: () =>
      returnsAccountabilityService.selectAll({
        searchtext: searchText ?? null,
        pagenumber: pageNumber,
        pagesize: pageSize,
        studyid: studyid ?? null,
        subjectno: subjectno ?? null,
        status: "A",
      }),
      enabled: !!returnSelectAll,
  });

  const verifyPinMutation = useMutation<ApiResponse, Error, any>({
        mutationFn: returnsAccountabilityService.verifyPin,
    });

  const DropdownQuery = useQuery<ApiResponse>({
      queryKey: ["study-ip-select-all", hasAllStudiesRight],
      queryFn: async () => {
        const result = await returnsAccountabilityService.dropdown({
          status: "A",
        });
        if (hasAllStudiesRight) {
          return result;
        }
        const user = GetUserData();
        const hasUserStudies = Array.isArray(user?.studies) && user.studies.length > 0;
        if (hasUserStudies && result && Array.isArray(result.data)) {
          const allowedIds = new Set(
            (user.studies as any[]).map((s: any) => String(s.studyid))
          );
          return {
            ...result,
            data: result.data.filter((study: any) => allowedIds.has(String(study.studyid))),
          };
        }
        return result;
      },
      enabled: !!getStudyDropDown, 
    });

  const subjectDropDown = useQuery<ApiResponse>({
      queryKey: ["subject-dropdown", studyid, ipcategory],
      queryFn: () =>
        returnsAccountabilityService.subjectDropdown({
          studyid,
          ipcategory,
        }),
      enabled: !!studyid && !!ipcategory, 
    });

  return {
    InsertMutation,
    UpdateMutation,
    DeleteMutation,
    SelectQuery,
    SelectAllQuery,
    verifyPinMutation,
    DropdownQuery,
    subjectDropDown,
  };
};